import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Client } from 'src/app/models/client.model';
import { DataService } from 'src/app/services/data.service';
import { SnackService } from 'src/app/services/snack.service';
import { SurveyConfComponent } from './survey-conf/survey-conf.component';
import { SetMainProviderDialogComponent } from './set-main-provider-dialog/set-main-provider-dialog.component';
import { ClientServicesService } from 'src/app/services/admin/client-services.service';
import { RoleModel } from 'src/app/services/models/role-model.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { SetAwvEmailTemplateComponent } from './set-awv-email-template/set-awv-email-template.component';
import { TwilioClientConfigDialogComponent } from './twilio-client-config-dialog/twilio-client-config-dialog.component';
@Component({
  selector: 'app-client-demographics',
  templateUrl: './client-demographics.component.html',
  styleUrls: ['./client-demographics.component.scss'],
})
export class ClientDemographicsComponent implements OnInit, OnDestroy {
  @Input() client: Client;
  @Output() clearClient = new EventEmitter<void>();

  unsubscribe$ = new Subject<void>();
  clientUpdated: Client;
  constructor(
    public data: DataService,
    private clientService: ClientServicesService,
    private snackBarService: SnackService,
    private dialog: MatDialog,
    private router: Router,
    private roleModel: RoleModel
  ) {}

  /**
   * Both `Set up rates` and `Bulk transfer` navigate into /admin, which RoleGuard restricts to
   * ISADMIN. This component is also rendered under /practice, where a practice admin would see the
   * buttons and bounce off the guard, so they are hidden instead of left as dead ends.
   */
  get isAdmin(): boolean {
    return this.roleModel.isAdmin();
  }
  ngOnInit(): void {
    this.updateClient();
  }

  closeDetail(): void {
    this.clearClient.emit();
  }

  updateClient(): void {
    this.clientService
      .getClientsById(this.client.client_id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((client) => {
        this.client = client;
      });
  }

  copyKey2Clipboard(value: string): void {
    navigator.clipboard.writeText(value);
    this.snackBarService.genericSnackBar(`Client id ${value} copied!`, ['success-snackbar'], 2000);
  }

  openSurveyConfiguration(): void {
    this.dialog.open(SurveyConfComponent, {
      width: '600px',
      data: { client: this.client },
    });
  }

  openSetMainProvider(): void {
    this.dialog.open(SetMainProviderDialogComponent, {
      width: '600px',
      data: { client: this.client },
    });
  }

  openSetUpRates(): void {
    this.router.navigate(['/admin/clients', this.client.client_id, 'rates']);
  }

  openBulkTransfer(): void {
    // As a route the client id is no longer implicit in the dialog data: without it the page would
    // load on /admin/clients/undefined/bulk-transfer and look functional.
    if (!this.client?.client_id) {
      this.snackBarService.genericSnackBar('Missing client id', ['error-snackbar'], 4000);
      return;
    }
    this.router.navigate(['/admin/clients', this.client.client_id, 'bulk-transfer']);
  }

   openTwilioNumberConfiguration(): void {
    this.dialog.open(TwilioClientConfigDialogComponent, {
      width: '700px',
      maxHeight: '90%',
      // Without this the container caps at 90% and simply clips: the dialog grows a row per phone
      // number, and past two or three the actions sit below the fold with no way to scroll to Save.
      // The sibling dialogs that set maxHeight pair it with this class for the same reason.
      panelClass: 'welby-modal-scroll',
      data: { client: this.client },
    });
  }

  openSetAWVEmailTemplate(): void {
    this.dialog.open(SetAwvEmailTemplateComponent, {
      width: '700px',
      maxHeight: '90%',
      data: { client: this.client },
      panelClass: 'welby-modal-scroll',
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
